import { createEventEmitter } from "@/lib/eventEmitter";
import { generateUUID } from "@/lib/uuid";
import { resolveAuthHeaders } from "./auth-strategy";
import { getWebsocketCredentialsProvider } from "./websocket-config";
import type {
  ConnectionStatus,
  FiinderApi,
  Message,
  Source,
  SourceLocator,
  SourcesTranslationPayload,
  ThinkingProcessUpdatePayload,
  TranslatedSourcePayload,
} from "./fiinder-api-types";
import type { components } from "./public-api-types";
import { WebsocketConnection } from "./websocket-connection";

// Extend the Window interface to include FIINDER_API_URL, FIINDER_COMPANY_TOKEN, FIINDER_AGENT_NAME, FIINDER_AGENT_ROLE, FIINDER_AGENT_AVATAR_URL, and FIINDER_COMPANY_MAIN_COLOR
declare global {
  interface Window {
    FIINDER_API_URL?: string;
    FIINDER_COMPANY_TOKEN?: string;
    FIINDER_AGENT_NAME?: string;
    FIINDER_AGENT_ROLE?: string;
    FIINDER_AGENT_AVATAR_URL?: string;
    FIINDER_COMPANY_MAIN_COLOR?: string;
    FIINDER_SET_TENANT_USER_ID?: (value: unknown) => void;
    FIINDER_LLM_MODEL?: string;
    // FIINDER_ENABLE_EXPERIMENTAL_FEATURES?: boolean
  }
}

// Helper to get API URL and company token from window
function getApiUrl(): string {
  if (typeof window === "undefined" || !window.FIINDER_API_URL) {
    throw new Error("FIINDER_API_URL is not set on window");
  }
  return window.FIINDER_API_URL;
}

function getConfiguredLlmModel(): string | undefined {
  if (typeof window === "undefined") return undefined;
  const model = window.FIINDER_LLM_MODEL?.trim();
  return model && model.length > 0 ? model : undefined;
}

// Helper to map API message (Message or AileenMessage) to local Message type
function isAileenMessage(
  msg: any
): msg is components["schemas"]["AileenMessage"] {
  return msg && msg.type === "aileen_message";
}

function mapApiMessage(
  apiMsg:
    | components["schemas"]["Message"]
    | components["schemas"]["AileenMessage"]
): Message {
  const base: Message = {
    id: apiMsg.message_id,
    role:
      isAileenMessage(apiMsg) || apiMsg.sender_id === "aileen"
        ? "agent"
        : "user",
    content: apiMsg.content,
    roomId: apiMsg.room_id,
    timestamp: apiMsg.created_at ?? new Date().toISOString(),
  };
  if (isAileenMessage(apiMsg)) {
    const msgs = apiMsg.follow_up_messages?.map((f) => f.content);
    // Fallback in case backend exposes `follow_up_questions`
    const questions = (apiMsg as any).follow_up_questions as
      | string[]
      | undefined;
    const list = (msgs ?? questions ?? []).filter(Boolean);
    base.followUps = list.length ? list : undefined;
  }
  return base;
}

// Check if user is logged in by calling /auth/verify
// async function isUserLogged(): Promise<boolean> {}
//   try {
//     const res = await fetch(`${getApiUrl()}/auth/verify`, {
//       method: "GET",
//       headers: {
//         Authorization: `Bearer ${getCompanyToken()}`,
//         "Content-Type": "application/json",
//       },
//     })

//     const data: components["schemas"]["TokenVerifyResponse"] = await res.json()
//     if (!res.ok || !data.valid) return false
//     return !!data.valid
//   } catch (e) {
//     return false
//   }
// }

const emitter = createEventEmitter<"message", Message>();

export const ChatEvents = {
  AILEEN_MESSAGE_STREAM: "aileen_message_stream",
  THINKING_PROCESS_UPDATE: "thinking_process_update",
  SOURCES_TRANSLATION: "sources_translation",
  ROOMS_UPDATES: "rooms_updates",
  MESSAGES_UPDATES: "messages_updates",
  ROOM_SUBSCRIPTIONS_UPDATES: "room_subscriptions_updates",
  SEND_MESSAGE: "send_message",
} as const;

const thinkingProcessEmitter = createEventEmitter<"thinkingProcess", ThinkingProcessUpdatePayload>();

/**
 * Shared emitter that publishes runtime changes to the tenant user id.
 * The React provider subscribes to this emitter; the host page mutates the
 * identity by invoking the global `window.FIINDER_SET_TENANT_USER_ID`
 * helper exposed below.
 */
export const tenantUserIdEmitter = createEventEmitter<"change", string | undefined>();

if (typeof window !== "undefined") {
  window.FIINDER_SET_TENANT_USER_ID = (value: unknown) => {
    const normalized = value ? String(value) : undefined;
    tenantUserIdEmitter.emit("change", normalized);
  };
}

// Cache to store thinking process data by message_id
// This allows the hook to retrieve data even if it subscribes after the event was emitted
const thinkingProcessCache = new Map<string, ThinkingProcessUpdatePayload>();

// Store translations that arrive before THINKING_PROCESS_UPDATE (race condition handling)
// When SOURCES_TRANSLATION arrives before the cache is populated, we store it here
// and merge it when THINKING_PROCESS_UPDATE eventually arrives
const pendingTranslations = new Map<string, SourcesTranslationPayload>();

export function getThinkingProcessFromCache(messageId: string): ThinkingProcessUpdatePayload | undefined {
  return thinkingProcessCache.get(messageId);
}

export async function sendMessage(
  {
    message,
    roomId,
    tenantUserId,
  }: { message: string; roomId: string; tenantUserId?: string },
  wsConn: WebsocketConnection
) {
  /**
   * Sends a chat message through the websocket `send_message` event.
   * Optionally carries `tenant_user_id` so the backend can attribute the
   * message to a specific visitor identity instead of falling back to the
   * master account.
   */
  if (!wsConn) {
    console.error("WebSocket is not connected");
    return null;
  }
  const requestId = generateUUID();
  const model = getConfiguredLlmModel();

  const payload: Record<string, string> = {
    message,
    room_id: roomId,
    request_id: requestId,
    ...(model ? { model } : {}),
  };
  if (tenantUserId) payload.tenant_user_id = tenantUserId;

  try {
    // Send message directly via websocket using the SEND_MESSAGE event
    const response = await wsConn.sendJson(
      {
        type: "send_message",
        payload: payload,
      },
      requestId
    );

    // Return the message_id from the server response
    return {
      message_id: response.message_id,
    };
  } catch (error) {
    console.error("Error sending message via websocket:", error);
    return null;
  }
}

export async function oldSendMessage(
  {
    message,
    roomId,
    tenantUserId,
  }: { message: string; roomId: string; tenantUserId?: string },
  wsConn: WebsocketConnection
) {
  /**
   * Sends a chat message through the REST-over-websocket bridge. Mirrors
   * `sendMessage` and propagates `tenant_user_id` in the request params
   * when provided.
   */
  if (!wsConn) {
    console.error("WebSocket is not connected");
    return null;
  }

  const requestId = generateUUID();
  const model = getConfiguredLlmModel();

  const payload: Record<string, string> = {
    message,
    room_id: roomId,
    ...(model ? { model } : {}),
  };
  if (tenantUserId) payload.tenant_user_id = tenantUserId;

  // console.trace()

  try {
    const response = await wsConn.sendRestRequest({
      requestId,
      path: "/conversation/send-message",
      method: "POST",
      params: payload,
    });

    return {
      message_id: response.message_id as string,
    };
  } catch (error) {
    console.error("Error sending message to existing room:", error);
    return null;
  }
}

// const wsConn = await initWebSocket()
export function getLegacyFiinderApi(
  wsConnect: Promise<WebsocketConnection>
): FiinderApi {
  const connectionStatusListeners = new Set<
    (status: ConnectionStatus) => void
  >();
  let latestConnectionStatus: ConnectionStatus = "connecting";

  wsConnect
    .then((ws) => {
      function handleStatusChange(status: ConnectionStatus) {
        latestConnectionStatus = status;
        const listeners = Array.from(connectionStatusListeners);
        listeners.forEach((listener) => listener(status));
      }

      latestConnectionStatus = ws.getConnectionStatus();
      handleStatusChange(latestConnectionStatus);
      ws.onConnectionStatusChange(handleStatusChange);
    })
    .catch((error) => {
      console.error("Failed to initialize connection status listener", error);
      latestConnectionStatus = "disconnected";
    });

  return {
    messageEmitter: emitter,
    thinkingProcessEmitter: thinkingProcessEmitter,
    tenantUserIdEmitter: tenantUserIdEmitter,
    async sendMessage(props: {
      message: Pick<Message, "content">;
      roomId: string | undefined;
      tenantUserId?: string;
    }) {
      if (!props.roomId) throw new Error("roomId is required");
      const res = await oldSendMessage(
        {
          message: props.message.content,
          roomId: props.roomId,
          tenantUserId: props.tenantUserId,
        },
        await wsConnect
      );

      return res?.message_id ?? "";
    },

    async fetchMessages({
      roomId,
      tenantUserId,
    }: {
      roomId: string | undefined;
      tenantUserId?: string;
    }) {
      if (!roomId) return [];
      const params = new URLSearchParams({ room_id: roomId });
      if (tenantUserId) params.set("tenant_user_id", tenantUserId);
      const url = `${getApiUrl()}/conversation/messages?${params.toString()}`;
      const headers = await resolveAuthHeaders({
        url,
        method: "GET",
      });
      const res = await fetch(url, {
        method: "GET",
        headers,
      });

      if (!res.ok) {
        console.error("Failed to fetch messages:", res.status, res.statusText);
        return [];
      }

      const data = await res.json();
      const messages = data?.messages ?? data ?? [];
      return Array.isArray(messages) ? messages.map(mapApiMessage) : [];
    },

    async requestNewRoomId() {
      const url = `${getApiUrl()}/conversation/request-new-room-id`;
      const headers = await resolveAuthHeaders({
        url,
        method: "POST",
      });
      const res = await fetch(url, {
        method: "POST",
        headers,
      });

      if (!res.ok) {
        console.error("Failed to create room:", res.status, res.statusText);
        throw new Error(`Failed to create room: ${res.status}`);
      }

      const data: { room_id: string } = await res.json();
      return data.room_id;
    },

    async submitFeedback({ messageId, score }) {
      const url = `${getApiUrl()}/feedback/register`;
      const headers = await resolveAuthHeaders({
        url,
        method: "POST",
      });
      headers["Content-Type"] = "application/json";
      const res = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify({
          message_id: messageId,
          score,
        }),
      });
      if (!res.ok) {
        console.error("Failed to submit feedback:", res.status, res.statusText);
        throw new Error(`Failed to submit feedback: ${res.status}`);
      }
    },

    getConnectionStatus() {
      return latestConnectionStatus;
    },

    subscribeToConnectionStatus(listener) {
      connectionStatusListeners.add(listener);
      listener(latestConnectionStatus);
      return () => {
        connectionStatusListeners.delete(listener);
      };
    },
  };
}

export function getFiinderApi(
  wsConnect: Promise<WebsocketConnection>
): FiinderApi {
  const connectionStatusListeners = new Set<
    (status: ConnectionStatus) => void
  >();
  let latestConnectionStatus: ConnectionStatus = "connecting";

  wsConnect
    .then((ws) => {
      function handleStatusChange(status: ConnectionStatus) {
        latestConnectionStatus = status;
        const listeners = Array.from(connectionStatusListeners);
        listeners.forEach((listener) => listener(status));
      }

      latestConnectionStatus = ws.getConnectionStatus();
      handleStatusChange(latestConnectionStatus);
      ws.onConnectionStatusChange(handleStatusChange);
    })
    .catch((error) => {
      console.error("Failed to initialize connection status listener", error);
      latestConnectionStatus = "disconnected";
    });

  return {
    messageEmitter: emitter,
    thinkingProcessEmitter: thinkingProcessEmitter,
    tenantUserIdEmitter: tenantUserIdEmitter,
    async sendMessage(props: {
      message: Pick<Message, "content">;
      roomId: string | undefined;
      tenantUserId?: string;
    }) {
      if (!props.roomId) throw new Error("roomId is required");
      const res = await sendMessage(
        {
          message: props.message.content,
          roomId: props.roomId,
          tenantUserId: props.tenantUserId,
        },
        await wsConnect
      );

      return res?.message_id ?? "";
    },

    async fetchMessages({
      roomId,
      tenantUserId,
    }: {
      roomId: string | undefined;
      tenantUserId?: string;
    }) {
      if (!roomId) return [];
      const params = new URLSearchParams({ room_id: roomId });
      if (tenantUserId) params.set("tenant_user_id", tenantUserId);
      const url = `${getApiUrl()}/conversation/messages?${params.toString()}`;
      const headers = await resolveAuthHeaders({
        url,
        method: "GET",
      });
      headers["Content-Type"] = "application/json";
      const res = await fetch(url, {
        headers,
      });
      if (!res.ok) throw new Error("Failed to fetch messages");
      const data: Array<
        | components["schemas"]["Message"]
        | components["schemas"]["AileenMessage"]
      > = await res.json();

      return Array.isArray(data) ? data.map(mapApiMessage) : [];
    },

    // async sendMessage({ roomId, message }) {
    //   if (!roomId) throw new Error("roomId is required")

    //   const body: components["schemas"]["SendMessageRequest"] = {
    //     room_id: roomId,
    //     message: message.content,
    //   }

    //   // const path = window.FIINDER_ENABLE_EXPERIMENTAL_FEATURES
    //   //   ? "/conversation/experimental/send-message"
    //   //   : "/conversation/send-message"
    //   const path = "/conversation/send-message"

    //   const res = await fetch(`${getApiUrl()}${path}`, {
    //     method: "POST",
    //     headers: {
    //       Authorization: `Bearer ${getCompanyToken()}`,
    //       "Content-Type": "application/json",
    //     },
    //     body: JSON.stringify(body),
    //   })

    //   if (!res.ok) throw new Error("Failed to send message")
    //   const data = await res.json()
    //   // Optionally emit the message (if needed)
    //   // emitter.emit("message", { ...message, id: data.message_id, roomId })
    //   return data.message_id || data.id
    // },

    async requestNewRoomId() {
      const url = `${getApiUrl()}/conversation/request-new-room-id`;
      const headers = await resolveAuthHeaders({
        url,
        method: "POST",
      });
      headers["Content-Type"] = "application/json";
      const res = await fetch(url, {
        method: "POST",
        headers,
      });
      if (!res.ok) throw new Error("Failed to request new room id");
      const data: components["schemas"]["RoomIdResponse"] = await res.json();
      return data.room_id;
    },

    async submitFeedback({ messageId, score }) {
      const url = `${getApiUrl()}/feedback/register`;
      const headers = await resolveAuthHeaders({
        url,
        method: "POST",
      });
      headers["Content-Type"] = "application/json";
      const res = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify({
          message_id: messageId,
          score,
        }),
      });
      if (!res.ok) {
        throw new Error(`Failed to submit feedback: ${res.status}`);
      }
    },

    // 3.1 Login implementation
    // async login() {
    //   const res = await fetch(`${getApiUrl()}/auth/login`, {
    //     method: "POST",
    //     // credentials: "include",
    //     headers: {
    //       Authorization: `Bearer ${getCompanyToken()}`,
    //     },
    //   })
    //   // console.log("res", await res.json())
    //   if (!res.ok) throw new Error("Login failed")

    //   // The access token is set as a cookie; nothing to return
    // },

    // isUserLogged,
    getConnectionStatus() {
      return latestConnectionStatus;
    },
    subscribeToConnectionStatus(listener) {
      connectionStatusListeners.add(listener);
      listener(latestConnectionStatus);
      return () => {
        connectionStatusListeners.delete(listener);
      };
    },
  };
}

export interface Entity {
  type: string;
}

export interface StreamingSource {
  url: string;
  fetch_url?: string | null;
  title?: string | null;
  description?: string | null;  // Changed from snippet
  keywords?: string[] | null;
  og_image?: string | null;
  snippet?: string | null;      // Deprecated - kept for backwards compatibility
  locator?: SourceLocator | null;  // Where the cited passage is inside the document
  instance_id?: string | null;
  leaf_key?: string | null;
}

/**
 * Projects the sources of an event onto the sources of a message.
 *
 * Sources without a title are left out, as they always were, and the address
 * of the cited passage travels along: it is what tells the card at which
 * second the player opens.
 */
export function toMessageSources({
  sources,
}: {
  sources: (StreamingSource | Source)[];
}): Source[] {
  return sources
    .filter((s) => s.title != null)
    .map((s) => ({
      url: s.url,
      fetch_url: s.fetch_url ?? undefined,
      title: s.title ?? undefined,
      description: s.description ?? undefined,
      keywords: s.keywords ?? undefined,
      og_image: s.og_image ?? undefined,
      snippet: s.snippet ?? undefined,
      locator: s.locator ?? undefined,
      instance_id: s.instance_id ?? undefined,
      leaf_key: s.leaf_key ?? undefined,
    }));
}

/**
 * Applies a `SOURCES_TRANSLATION` payload onto a sources list, matched by url.
 *
 * Two distinct documents (their identity, `instance_id`, differs) can share
 * a public url; the translation payload carries no `instance_id` to
 * disambiguate them. A url that is not unique within `sources` is left
 * untranslated rather than applying one document's translation to another's.
 */
export function mergeTranslatedSources<T extends { url: string; title?: string | null; description?: string | null }>({
  sources,
  translations,
}: {
  sources: T[];
  translations: TranslatedSourcePayload[];
}): T[] {
  const urlCounts = new Map<string, number>();
  for (const source of sources) {
    urlCounts.set(source.url, (urlCounts.get(source.url) ?? 0) + 1);
  }

  const translatedMap = new Map<string, TranslatedSourcePayload>();
  for (const translated of translations) {
    if (urlCounts.get(translated.url) === 1) {
      translatedMap.set(translated.url, translated);
    }
  }

  return sources.map((source) => {
    const translated = translatedMap.get(source.url);
    if (!translated) return source;
    return {
      ...source,
      title: translated.title ?? source.title,
      description: translated.description ?? source.description,
    };
  });
}

export interface StreamingMessage extends Entity {
  type: "streaming_message";
  message_id: string;
  room_id: string;
  sender_id?: string;
  delta: string;
  delta_generation_finished: boolean;
  follow_up_messages?: string[] | null;
  timestamp?: string;
  sources?: StreamingSource[] | null;
  queries?: string[] | null;
  stream_is_finished?: boolean;
}

export type RestApiMessage = components["schemas"]["Message"];
export type AileenMessage = components["schemas"]["AileenMessage"];
export type RoomSubscription = components["schemas"]["RoomSubscription"];
export type Room = components["schemas"]["Room"];
export type UserBasicInfo = components["schemas"]["UserBasicInfo"];

export interface GetSubscriptionsResponse {
  rooms: Room[];
  subscriptions: RoomSubscription[];
  rooms_last_messages: RestApiMessage[];
}
type MessageType = RestApiMessage | AileenMessage;

export type EventsPayloads = {
  [ChatEvents.AILEEN_MESSAGE_STREAM]: StreamingMessage;

  [ChatEvents.THINKING_PROCESS_UPDATE]: ThinkingProcessUpdatePayload;

  [ChatEvents.SOURCES_TRANSLATION]: SourcesTranslationPayload;

  [ChatEvents.ROOMS_UPDATES]: {
    rooms: Array<Room>;
  };

  [ChatEvents.ROOM_SUBSCRIPTIONS_UPDATES]: {
    room_subscriptions: Array<RoomSubscription>;
  };

  [ChatEvents.MESSAGES_UPDATES]: {
    messages: Array<MessageType>;
  };
};

export type Events<T extends keyof EventsPayloads> = {
  type: T;
  payload: EventsPayloads[T];
};

// --- WebSocket Integration ---

export function getWebsocketBaseUrl(): string {
  const apiUrl = getApiUrl();
  return apiUrl.replace(/^http/, "ws") + "/websocket/ws";
}

export function buildWebsocketUrl(
  accessToken: string,
  dpopProof?: string
): string {
  const baseUrl = getWebsocketBaseUrl();
  const params = new URLSearchParams({ access_token: accessToken });
  if (dpopProof) {
    params.append("dpop", dpopProof);
  }
  return `${baseUrl}?${params.toString()}`;
}

export async function initWebSocket() {
  const wsConn = new WebsocketConnection();
  const getCredentials = getWebsocketCredentialsProvider();

  const buildUrl = async () => {
    const { accessToken, dpopProof } = await getCredentials();
    return buildWebsocketUrl(accessToken, dpopProof);
  };

  const wsUrl = await buildUrl();
  const accumulation: Record<string, string> = {};
  // Track which messageIds we've emitted placeholder messages for
  const placeholderEmitted = new Set<string>();
  const sourcesAccumulation: Record<string, Source[]> = {};
  const queriesAccumulation: Record<string, string[]> = {};

  wsConn.on(
    ChatEvents.AILEEN_MESSAGE_STREAM,
    (event: Events<typeof ChatEvents.AILEEN_MESSAGE_STREAM>) => {
      accumulation[event.payload.message_id] =
        accumulation[event.payload.message_id] || "";
      accumulation[event.payload.message_id] += event.payload.delta;

      const rawFollowUps =
        (event.payload as any).follow_up_messages ||
        (event.payload as any).follow_up_questions;

      // Accumulate sources and queries from stream (fallback for legacy AILEEN_MESSAGE_STREAM)
      if (event.payload.sources && event.payload.sources.length > 0) {
        sourcesAccumulation[event.payload.message_id] = toMessageSources({
          sources: event.payload.sources,
        });
      }

      if (event.payload.queries && event.payload.queries.length > 0) {
        queriesAccumulation[event.payload.message_id] = event.payload.queries;
      }

      const msg: Message = {
        id: event.payload.message_id,
        role: event.payload.sender_id === "aileen" ? "agent" : "user",
        content: accumulation[event.payload.message_id],
        roomId: event.payload.room_id,
        timestamp: event.payload.timestamp ?? new Date().toISOString(),
        // Stream payload may include follow up suggestions
        followUps:
          Array.isArray(rawFollowUps) && rawFollowUps.length
            ? rawFollowUps
            : undefined,
        // Include sources and queries if available
        sources: sourcesAccumulation[event.payload.message_id],
        queries: queriesAccumulation[event.payload.message_id],
        // True while the message is still being streamed
        isStreaming: !event.payload.stream_is_finished,
        // ID of the user message that triggered this response
        userMessageId: (event.payload as any).user_message_id,
      };

      emitter.emit("message", msg);

      // Clean up accumulation objects when stream is finished to prevent memory leaks
      if (event.payload.stream_is_finished) {
        delete accumulation[event.payload.message_id];
        delete sourcesAccumulation[event.payload.message_id];
        delete queriesAccumulation[event.payload.message_id];
        placeholderEmitted.delete(event.payload.message_id);
        pendingTranslations.delete(event.payload.message_id);
        thinkingProcessCache.delete(event.payload.message_id);
      }
    }
  );

  wsConn.on(ChatEvents.MESSAGES_UPDATES, (event) => {
    for (const msg of event.payload.messages) {
      emitter.emit("message", mapApiMessage(msg));
    }
  });

  // Handler for THINKING_PROCESS_UPDATE event
  wsConn.on(
    ChatEvents.THINKING_PROCESS_UPDATE,
    (event: Events<typeof ChatEvents.THINKING_PROCESS_UPDATE>) => {
      let payload = event.payload;

      // Check if there are pending translations that arrived before this event (race condition)
      const pending = pendingTranslations.get(payload.message_id);

      if (pending && pending.sources && pending.sources.length > 0 && payload.sources && payload.sources.length > 0) {
        // Merge pending translations into sources
        const mergedSources = mergeTranslatedSources({
          sources: payload.sources,
          translations: pending.sources,
        });

        payload = {
          ...payload,
          sources: mergedSources,
        };

        // Clear pending translations after merging
        pendingTranslations.delete(payload.message_id);
      }

      // Store sources/queries in accumulation for backward compatibility with message.sources
      if (payload.sources && payload.sources.length > 0) {
        sourcesAccumulation[payload.message_id] = toMessageSources({
          sources: payload.sources,
        });
      }

      if (payload.queries && payload.queries.length > 0) {
        queriesAccumulation[payload.message_id] = payload.queries;
      }

      // Store in cache before emitting
      thinkingProcessCache.set(payload.message_id, payload);

      // Emit placeholder message so agent message appears in UI immediately
      // This allows ThinkingProcess to show right away
      if (!placeholderEmitted.has(payload.message_id)) {
        placeholderEmitted.add(payload.message_id);
        const placeholderMsg: Message = {
          id: payload.message_id,
          role: "agent",
          content: "",
          roomId: payload.room_id,
          timestamp: new Date().toISOString(),
          isStreaming: true,
          userMessageId: payload.user_message_id,
        };
        emitter.emit("message", placeholderMsg);
      }

      // Emit the thinking process update
      thinkingProcessEmitter.emit("thinkingProcess", payload);
    }
  );

  // Handler for SOURCES_TRANSLATION event
  // Merges translated sources into the thinking process cache
  // Handles race condition where translations may arrive before THINKING_PROCESS_UPDATE
  wsConn.on(
    ChatEvents.SOURCES_TRANSLATION,
    (event: Events<typeof ChatEvents.SOURCES_TRANSLATION>) => {
      const payload = event.payload;
      const messageId = payload.message_id;

      // Get existing thinking process data from cache
      const existingData = thinkingProcessCache.get(messageId);
      if (!existingData) {
        // Race condition: SOURCES_TRANSLATION arrived before THINKING_PROCESS_UPDATE
        // Store translations for later merge when THINKING_PROCESS_UPDATE arrives
        pendingTranslations.set(messageId, payload);
        return;
      }

      // Merge translated sources into existing sources
      const updatedSources = mergeTranslatedSources({
        sources: existingData.sources,
        translations: payload.sources,
      });

      // Update cache with translated sources
      const updatedPayload: ThinkingProcessUpdatePayload = {
        ...existingData,
        sources: updatedSources,
      };
      thinkingProcessCache.set(messageId, updatedPayload);

      // Also update sourcesAccumulation for backward compatibility with message.sources
      if (sourcesAccumulation[messageId]) {
        sourcesAccumulation[messageId] = toMessageSources({
          sources: updatedSources,
        });
      }

      // Emit updated thinking process to trigger UI re-render
      thinkingProcessEmitter.emit("thinkingProcess", updatedPayload);
    }
  );

  wsConn.connect(wsUrl, buildUrl).then(() => {
    console.log("WebSocket connected");
  });

  return wsConn;
}

export async function fetchLegacyWebsocketAccessToken(): Promise<string> {
  const url = `${getApiUrl()}/websocket/get_access_token`;
  const headers = await resolveAuthHeaders({
    url,
    method: "GET",
  });
  headers["Content-Type"] = "application/json";

  const res = await fetch(url, {
    method: "GET",
    headers,
  });
  const data = await res.json();
  if (!res.ok || !data.access_token) {
    throw new Error("Failed to get websocket access token");
  }
  return data.access_token;
}
